import { useMemo } from 'react';
import type { Task } from '../../types/tasks';
import { useTaskStore } from '../../stores/taskStore';
import { getAgentBySlug, getAgentColor } from '../../utils/agents';

const CADENCE_ORDER: Record<string, number> = {
  daily: 0,
  weekly: 1,
  biweekly: 2,
  monthly: 3,
  quarterly: 4,
  trigger: 5,
};

const CADENCE_STYLES: Record<string, string> = {
  daily: 'bg-accent-purple/15 text-accent-purple-light',
  weekly: 'bg-info/15 text-info',
  biweekly: 'bg-avatar-cyan/15 text-avatar-cyan',
  monthly: 'bg-success/15 text-success',
  quarterly: 'bg-warning/15 text-warning',
  trigger: 'bg-error/15 text-error',
};

const PRIORITY_STYLES: Record<string, string> = {
  urgent: 'bg-error/15 text-error',
  high: 'bg-warning/15 text-warning',
  medium: 'bg-info/15 text-info',
  low: 'bg-bg-tertiary text-text-muted',
};

const STATUS_INDICATOR: Record<string, string> = {
  backlog: 'bg-text-muted',
  in_progress: 'bg-info',
  completed: 'bg-success',
};

interface TaskAgentViewProps {
  onTaskClick: (task: Task) => void;
}

interface AgentGroup {
  slug: string;
  name: string;
  title: string;
  department: string;
  tasks: Task[];
}

export default function TaskAgentView({ onTaskClick }: TaskAgentViewProps) {
  const tasks = useTaskStore((s) => s.tasks);

  const agentGroups = useMemo(() => {
    const grouped = new Map<string, Task[]>();

    for (const task of tasks) {
      const key = task.assigned_agent || '__unassigned';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(task);
    }

    const groups: AgentGroup[] = [];

    for (const [slug, agentTasks] of grouped) {
      const agent = slug !== '__unassigned' ? getAgentBySlug(slug) : null;

      // Sort by cadence order, then priority
      agentTasks.sort((a, b) => {
        const cadA = CADENCE_ORDER[a.cadence || ''] ?? 99;
        const cadB = CADENCE_ORDER[b.cadence || ''] ?? 99;
        if (cadA !== cadB) return cadA - cadB;
        return 0;
      });

      groups.push({
        slug,
        name: agent?.name || 'Unassigned',
        title: agent?.title || '',
        department: agent?.department || '',
        tasks: agentTasks,
      });
    }

    // Sort groups: assigned agents alphabetically, unassigned last
    groups.sort((a, b) => {
      if (a.slug === '__unassigned') return 1;
      if (b.slug === '__unassigned') return -1;
      return a.name.localeCompare(b.name);
    });

    return groups;
  }, [tasks]);

  if (agentGroups.length === 0) {
    return (
      <div className="text-center py-12 text-text-muted text-sm">
        No tasks found
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {agentGroups.map((group) => {
        const agentData = group.slug !== '__unassigned' ? getAgentBySlug(group.slug) : null;
        const colorClass = agentData ? getAgentColor(agentData.department) : 'bg-bg-tertiary';

        return (
          <div key={group.slug} className="bg-bg-secondary border border-border-subtle rounded-lg overflow-hidden">
            {/* Agent Header */}
            <div className="flex items-center gap-3 p-4 border-b border-border-subtle">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium text-white ${colorClass}`}>
                {group.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-text-primary truncate">{group.name}</h3>
                  {group.title && (
                    <span className="text-xs text-text-muted truncate">{group.title}</span>
                  )}
                </div>
                {group.department && (
                  <p className="text-xs text-text-muted">{group.department}</p>
                )}
              </div>
              <span className="bg-bg-tertiary rounded-full px-2.5 py-0.5 text-xs text-text-muted">
                {group.tasks.length}
              </span>
            </div>

            {/* Task Rows */}
            <div className="divide-y divide-border-subtle">
              {group.tasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => onTaskClick(task)}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-bg-tertiary/50 cursor-pointer transition-colors"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onTaskClick(task);
                    }
                  }}
                >
                  {/* Status dot */}
                  <div className={`h-2 w-2 rounded-full shrink-0 ${STATUS_INDICATOR[task.status] || 'bg-text-muted'}`} />

                  {/* Title */}
                  <span className="text-sm text-text-primary truncate flex-1">{task.title}</span>

                  {/* Badges */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {task.cadence && (
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CADENCE_STYLES[task.cadence] || 'bg-bg-tertiary text-text-muted'}`}>
                        {task.cadence}
                      </span>
                    )}
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium}`}>
                      {task.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
